import {
  NOWHERE,
  PRESETS,
  UNITS,
  blockAround,
  buildPreset,
  expr as exprSource,
  identifyPreset,
  parseTemplate,
  serializeLine,
  setLine,
  withBlockAround,
  type AutoLine,
  type Dim,
  type DynamicLine,
  type Expr,
  type Line,
  type Mods,
  type Preset,
  type SetLine as SetLineNode,
  type Unit,
} from '@gymcloud/mixedscript'

import { number } from '~/lib/template-tree'

/**
 * What a line of an exercise is, as data: which of the shapes it takes, how it reads, and how
 * it is written back. None of it draws anything — the editor beside it does that — so a line
 * can be reasoned about, and tested, without one.
 */

/**
 * What a line asks for around its sets — whether they are warm-ups, the rest before them,
 * whether they ask to be filmed, anything written on them. A static line and a ramp carry it
 * themselves; a progression carries it on the set line it yields. A code line is the one
 * exception: its own `yield` lines say all of this, and a second place to say it would only
 * disagree with the first.
 */
export function around(
  line: Line,
  identified: { preset: Preset } | null,
): { warmup: boolean; mods: Mods } | null {
  if (line.type === 'dynamic') {
    return identified === null ? null : blockAround(line)
  }
  return { warmup: line.warmup, mods: line.mods }
}

export function withAround(line: Line, next: { warmup: boolean; mods: Mods }): Line {
  return line.type === 'dynamic'
    ? withBlockAround(line, next)
    : { ...line, warmup: next.warmup, mods: next.mods }
}

// --- what a line can be ---

export type Mode =
  | { readonly kind: 'static'; readonly dim: Dim }
  | { readonly kind: 'progression'; readonly preset: Preset }
  | { readonly kind: 'ramp' }
  | { readonly kind: 'custom' }

export const MODES: Mode[] = [
  { kind: 'static', dim: 'none' },
  { kind: 'static', dim: 'time' },
  { kind: 'static', dim: 'distance' },
  ...PRESETS.map((preset): Mode => ({ kind: 'progression', preset })),
  { kind: 'ramp' },
  { kind: 'custom' },
]

export function labelOf(mode: Mode): string {
  switch (mode.kind) {
    // A static line is named by what it counts, so a line already set to one is named too —
    // it is read off the line's own dimension, not off the entry that was picked.
    case 'static':
      return primaryLabel(mode.dim)
    case 'progression':
      return mode.preset.label
    case 'ramp':
      return 'Ramp'
    case 'custom':
      return 'Code'
  }
}

export function describeMode(mode: Mode): string {
  switch (mode.kind) {
    case 'static':
      return mode.dim === 'time'
        ? 'Sets you hold for a time.'
        : mode.dim === 'distance'
          ? 'Sets you cover a distance in.'
          : 'Sets of a number of reps.'
    case 'progression':
      return mode.preset.id === 'double'
        ? 'Work your reps up through a range, then move the weight up.'
        : 'Move the weight up every time you clear all your sets.'
    case 'ramp':
      return 'Sets climbing up to your first working weight — warm-ups, unless you say otherwise.'
    case 'custom':
      return 'Work the sets out yourself, in code that can read your history.'
  }
}

export function modeOf(line: Line, identified: { preset: Preset } | null): Mode {
  if (line.type === 'auto') {
    return { kind: 'ramp' }
  }
  if (line.type === 'dynamic') {
    return identified ? { kind: 'progression', preset: identified.preset } : { kind: 'custom' }
  }
  return { kind: 'static', dim: dimOf(line) }
}

export function sameMode(left: Mode, right: Mode): boolean {
  if (left.kind === 'static' && right.kind === 'static') {
    return left.dim === right.dim
  }
  if (left.kind === 'progression' && right.kind === 'progression') {
    return left.preset.id === right.preset.id
  }
  return left.kind === right.kind
}

/** Switching a line to another mode keeps nothing but the shape of the new one. */
export function blankFor(mode: Mode, _from: Line): Line {
  switch (mode.kind) {
    case 'static':
      return {
        type: 'set',
        warmup: false,
        count: number(3, UNITS.reps),
        primary:
          mode.dim === 'time'
            ? number(45, UNITS.seconds)
            : mode.dim === 'distance'
              ? number(500, UNITS.meters)
              : number(5, UNITS.reps),
        weight: null,
        mods: {},
        comments: [],
        at: NOWHERE,
      }
    case 'progression':
      return buildPreset(mode.preset, mode.preset.defaults)
    case 'ramp':
      return {
        type: 'auto',
        warmup: true,
        args: [
          ['bar', number(20, UNITS.kg)],
          ['jump', number(20, UNITS.kg)],
          ['to', number(90, UNITS.ratio)],
        ],
        mods: {},
        comments: [],
        at: NOWHERE,
      }
    case 'custom':
      return lineFrom('dynamic "What this does" {\nyield 3 x 5 @ 60kg\n}')
  }
}

/** A new line takes the shape of the last one, which is usually another set of the same thing. */
export function defaultLine(lines: readonly Line[]): Line {
  for (let index = lines.length - 1; index >= 0; index -= 1) {
    const line = lines[index]
    if (line?.type === 'set' && !line.warmup) {
      return line
    }
  }
  return blankFor(
    { kind: 'static', dim: 'none' },
    { type: 'auto', warmup: true, args: [], mods: {}, comments: [], at: NOWHERE },
  )
}

/** What a line says on the board: its sets, or the progression it stands for. */
export function describeLine(line: Line): string {
  if (line.type === 'set') {
    return setLine(line)
  }
  if (line.type === 'auto') {
    return rampSummary(line)
  }
  const found = identifyPreset(line)
  return found ? found.preset.summary(found.params) : line.label || 'Custom progression'
}

export function rampSummary(line: AutoLine): string {
  const name = line.warmup ? 'Warm-up ramp' : 'Ramp'
  const bar = line.args.find(([name]) => name === 'bar')?.[1]
  const jump = line.args.find(([name]) => name === 'jump')?.[1]
  return bar && jump ? `${name} from ${exprSource(bar)}, +${exprSource(jump)} a set` : name
}

// --- reading a primary ---

type Primary =
  | { readonly kind: 'exact'; readonly dim: Dim }
  | { readonly kind: 'range'; readonly dim: Dim; readonly low: Expr; readonly high: Expr }
  | { readonly kind: 'amrap'; readonly dim: Dim; readonly low: Expr }
  | { readonly kind: 'expr'; readonly dim: Dim }

export function readPrimary(primary: Expr): Primary {
  if (primary.type === 'number') {
    return { kind: 'exact', dim: primary.unit.dim }
  }
  if (primary.type === 'call' && primary.callee.type === 'name') {
    const [first, second] = primary.args
    if (primary.callee.name === 'range' && first && second) {
      const dim = first.type === 'number' ? first.unit.dim : 'none'
      return { kind: 'range', dim, low: first, high: second }
    }
    if (primary.callee.name === 'amrap' && first) {
      return { kind: 'amrap', dim: 'none', low: first }
    }
  }
  return { kind: 'expr', dim: 'none' }
}

export function dimOf(line: SetLineNode): Dim {
  return readPrimary(line.primary).dim
}

export function primaryLabel(dim: Dim): string {
  return dim === 'time' ? 'Time' : dim === 'distance' ? 'Distance' : 'Reps'
}

/** What can be typed into a primary field, said as examples. */
export function primaryTips(dim: Dim): string {
  return dim === 'time'
    ? 'Try 45 for seconds, 2m for minutes, or 30-60 for anything in between.'
    : dim === 'distance'
      ? 'Try 500 for metres, 1km, or 400-800 for anything in between.'
      : 'Try 5, or 8-12 for anything in between, or 5+ for as many as you can manage.'
}

/** A primary as its field shows it: a range as 8-12, an amrap as 5+, the rest as written. */
export function primaryText(primary: Expr): string {
  const read = readPrimary(primary)
  if (read.kind === 'range' && read.low.type === 'number' && read.high.type === 'number') {
    return `${exprSource(read.low)}-${exprSource(read.high)}`
  }
  if (read.kind === 'amrap' && read.low.type === 'number') {
    return `${exprSource(read.low)}+`
  }
  return exprSource(primary)
}

/** A weight as its field shows it: a range as 20kg-30kg, the rest as written, '' when empty. */
export function weightText(weight: Expr | null): string {
  if (weight === null) {
    return ''
  }
  const read = readPrimary(weight)
  if (read.kind === 'range' && read.low.type === 'number' && read.high.type === 'number') {
    return `${exprSource(read.low)}-${exprSource(read.high)}`
  }
  return exprSource(weight)
}

export function primaryUnit(dim: Dim): Unit {
  return dim === 'time' ? UNITS.seconds : dim === 'distance' ? UNITS.meters : UNITS.reps
}

export function call(name: string, args: Expr[]): Expr {
  return { type: 'call', callee: { type: 'name', name, at: NOWHERE }, args, named: [], at: NOWHERE }
}

export function toExact(primary: Expr, read: Primary, unit: Unit): Expr {
  if (read.kind === 'range' || read.kind === 'amrap') {
    return read.low
  }
  if (read.kind === 'expr') {
    return number(unit.dim === 'time' ? 45 : unit.dim === 'distance' ? 500 : 5, unit)
  }
  return primary
}

export function toRange(primary: Expr, read: Primary, unit: Unit): Expr {
  if (read.kind === 'range') {
    return primary
  }
  const low = read.kind === 'amrap' ? read.low : read.kind === 'exact' ? primary : number(8, unit)
  const high =
    low.type === 'number'
      ? number(low.value + (unit.dim === 'time' ? 15 : unit.dim === 'distance' ? 100 : 4), low.unit)
      : low
  return call('range', [low, high])
}

export function toAmrap(primary: Expr, read: Primary): Expr {
  if (read.kind === 'amrap') {
    return primary
  }
  const low =
    read.kind === 'range' ? read.low : read.kind === 'exact' ? primary : number(5, UNITS.reps)
  return call('amrap', [low])
}

// --- a line as text ---

/** A line as text, which is what the code field edits and reads back. */
export function lineSource(line: Line): string {
  const out: string[] = []
  serializeLine(out, line, 0)
  return out.join('').trimEnd()
}

/**
 * What a block does, without the `dynamic ... { }` it does it in — the statements alone, at
 * the left margin. This is what a code field edits, so nothing anybody types can change what
 * kind of line they are editing.
 */
export function blockBody(line: DynamicLine): string {
  const written = lineSource(line).split('\n')
  return written
    .slice(1, -1)
    .map((held) => (held.startsWith('  ') ? held.slice(2) : held))
    .join('\n')
}

/** A block built back from its statements. Whatever they say, this is a block. */
export function blockFrom(body: string): DynamicLine {
  const parsed = lineFrom(`dynamic "x" {\n${body}\n}`)
  if (parsed.type !== 'dynamic') {
    throw new Error('That is not a block')
  }
  return parsed
}

/** One line parsed on its own, by giving it the template it needs to sit in. */
export function lineFrom(text: string): Line {
  const wrapped = `template "t" {\nworkout "w" {\nexercise "e" {\n${text}\n}\n}\n}`
  const item = parseTemplate(wrapped).workouts[0]?.items[0]
  const first = item?.type === 'exercise' ? item.exercise.lines[0] : undefined
  if (!first) {
    throw new Error('That is not a line')
  }
  return first
}
